import { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Input,
  Button,
  Form,
  Collapse,
  message,
  Empty,
  Tooltip,
  Dropdown,
  Modal,
  Segmented,
  Select,
} from "antd";
import DashboardLayout from "layout/DashboardLayout";
import PageTitle from "components/PageTitle";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiService } from "api/api.service";
import {
  TbChevronRight,
  TbDotsVertical,
  TbFileText,
  TbMail,
  TbPhone,
  TbPlus,
  TbRefresh,
  TbTrash,
} from "react-icons/tb";
import { ChannelType, IChannel, IDesk } from "dtos/communication-channel.dto";
import { T_MongoId } from "types/_utils.types";
import { TiTickOutline } from "react-icons/ti";
import { useQueryClient } from "@tanstack/react-query";
import { phoneFormatter, timeAgo } from "utils/functions";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { OrgSelector } from "components/Organisation/OrganisationSelector";
import { useForm } from "antd/es/form/Form";
const { Text } = Typography;

type ConnStatus = "idle" | "connected" | "error" | "loading";

type StatusStyleT = {
  bg: string;
  color: string;
  text: string;
};

type GoogleValues = { email: string; appPassword: string; alias?: string };
type MicrosoftValues = { email: string; tenantId: string; clientId: string; clientSecret: string; alias?: string };
type JsonValues = {
  file: File | null;
  impersonateEmails: string[];
};
type TwilioValues = {
  sid: string;
  token: string;
  messagingServiceSid?: string;
  numbers: { number: string; alias?: string }[];
};

function statusStyle(status: ConnStatus): StatusStyleT {
  switch (status) {
    case "connected":
      return { bg: "oklch(0.94 0.06 145)", color: "oklch(0.4 0.15 145)", text: "Connected" };
    case "error":
      return { bg: "oklch(0.95 0.04 25)", color: "oklch(0.5 0.18 25)", text: "Error" };
    case "loading":
      return { bg: "oklch(0.94 0.03 255)", color: "oklch(0.45 0.12 255)", text: "Connecting…" };
    default:
      return { bg: "oklch(0.93 0.005 240)", color: "oklch(0.55 0.01 255)", text: "Not connected" };
  }
}

function borderForStatus(status: ConnStatus, open: boolean): string {
  if (open) return "oklch(0.75 0.08 255)";
  if (status === "connected") return "oklch(0.82 0.08 145)";
  if (status === "error") return "oklch(0.82 0.1 25)";
  return "oklch(0.88 0.01 240)";
}

const inputStyle: React.CSSProperties = {
  borderRadius: 8,
  border: "1px solid oklch(0.88 0.01 240)",
  fontSize: 14,
  color: "oklch(0.15 0.02 255)",
  background: "#fff",
};

const monoInputStyle: React.CSSProperties = {
  ...inputStyle,
  fontFamily: "'DM Mono', ui-monospace, monospace",
};

// ---------- Small shared icons ----------
function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="oklch(0.6 0.02 255)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function StatusPill({ status }: { status: ConnStatus }) {
  const s = statusStyle(status);
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full text-xs font-medium"
      style={{ padding: "4px 10px", background: s.bg, color: s.color }}
    >
      <div
        className="rounded-full"
        style={{
          width: 6,
          height: 6,
          background: s.color,
          animation: status === "loading" ? "dc-pulse 1s ease-in-out infinite" : "none",
        }}
      />
      {s.text}
    </div>
  );
}

// General (non field-level) error shown next to the Save button — e.g. a simulated
// server/auth failure, as opposed to per-field required-ness which antd Form handles itself.
function ServerError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs" style={{ color: "oklch(0.5 0.18 25)" }}>
      <ErrorIcon />
      {message}
    </div>
  );
}

function ComingSoonTag() {
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        padding: "2px 8px",
        borderRadius: 4,
        background: "oklch(0.92 0.006 240)",
        color: "oklch(0.55 0.01 255)",
        marginLeft: 8,
      }}
    >
      Coming Soon
    </span>
  );
}

function ProviderCard({
  icon,
  iconBg,
  title,
  subtitle,
  status,
  open,
  onToggle,
  children,
  disabled = false,
  comingSoon = false,
}: {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  subtitle: string;
  status: ConnStatus;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  comingSoon?: boolean;
}) {
  return (
    <div
      className="rounded-2xl bg-white overflow-hidden"
      style={{
        border: `1px solid ${borderForStatus(status, open)}`,
        transition: "box-shadow 0.2s",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "default",
      }}
    >
      <div
        onClick={disabled ? undefined : onToggle}
        className="flex items-center gap-4 cursor-pointer hover:bg-[oklch(0.98_0.005_240)]"
        style={{
          padding: "20px 24px",
          cursor: disabled ? "not-allowed" : "pointer",
          ...(disabled && { pointerEvents: "none" }),
        }}
      >
        <div
          className="flex items-center justify-center rounded-[10px] flex-shrink-0"
          style={{ width: 40, height: 40, background: iconBg }}
        >
          {icon}
        </div>
        <div className="flex-1">
          <Text style={{ fontSize: 15, fontWeight: 500, color: "oklch(0.15 0.02 255)", display: "block" }}>
            {title}
            {comingSoon && <ComingSoonTag />}
          </Text>
          <Text style={{ fontSize: 13, color: "oklch(0.55 0.02 255)", marginTop: 2, display: "block" }}>
            {subtitle}
          </Text>
        </div>
        <div className="flex items-center gap-2.5">
          <ChevronIcon open={open} />
        </div>
      </div>
      {open && !disabled && (
        <div
          className="border-t"
          style={{
            borderColor: "oklch(0.93 0.01 240)",
            padding: 24,
            background: "oklch(0.99 0.004 240)",
            animation: "dc-fade-slide-down 0.18s ease",
          }}
        >
          <div style={{ maxWidth: 480 }}>{children}</div>
        </div>
      )}
    </div>
  );
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 11, color: "oklch(0.6 0.02 255)" }}>{children}</span>;
}

// ---------- Brand icons ----------
function GoogleLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function TwilioLogo() {
  return (
    <svg width="22" height="22" viewBox="0 0 64 64" fill="oklch(0.55 0.22 25)">
      <circle cx="32" cy="32" r="30" />
      <circle cx="21" cy="21" r="7" fill="#fff" />
      <circle cx="43" cy="21" r="7" fill="#fff" />
      <circle cx="21" cy="43" r="7" fill="#fff" />
      <circle cx="43" cy="43" r="7" fill="#fff" />
    </svg>
  );
}

function ServiceJsonLogo() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="oklch(0.5 0.15 145)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="oklch(0.5 0.02 255)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="oklch(0.5 0.02 255)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16.92z" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="oklch(0.6 0.1 145)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

// Controlled dropzone, wired as a normal antd Form.Item field (value / onChange).
function JsonDropzone({ value, onChange }: { value?: File | null; onChange?: (file: File | null) => void }) {
  const fileName = value ? value.name : "Drop or click to upload JSON";
  return (
    <label
      className="flex flex-col items-center gap-2.5 cursor-pointer"
      style={{
        border: `2px dashed ${value ? "oklch(0.65 0.12 145)" : "oklch(0.82 0.02 240)"}`,
        borderRadius: 10,
        padding: "32px 24px",
        background: value ? "oklch(0.96 0.04 145)" : "oklch(0.985 0.004 240)",
        transition: "border-color 0.15s, background 0.15s",
      }}
    >
      <UploadIcon />
      <div className="text-center">
        <div style={{ fontSize: 14, fontWeight: 500, color: "oklch(0.3 0.02 255)" }}>{fileName}</div>
        <div style={{ fontSize: 12, color: "oklch(0.6 0.02 255)", marginTop: 4 }}>Click to upload .json file</div>
      </div>
      <input
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={(e) => onChange?.(e.target.files?.[0] ?? null)}
      />
    </label>
  );
}

// ─── Config ────────────────────────────────────────────────────────────────────

const CHANNEL_META: Record<ChannelType, { icon: React.ReactNode; label: string }> = {
  email: { icon: <MailIcon />, label: "Email" },
  sms: { icon: <PhoneIcon />, label: "SMS" },
  trsc: { icon: <TbFileText size={14} style={{ color: "oklch(0.55 0.015 255)" }} />, label: "Transcript" },
};

const TYPE_ORDER: ChannelType[] = ["email", "sms", "trsc"];

export function ChannelsSection({
  channels,
  desk_id,
  deskDetails,
  openModal,
  selectChannel,
}: {
  channels: IChannel[];
  desk_id: string;
  deskDetails?: IDesk;
  selectChannel: (val: IChannel) => void;
  openModal: () => void;
}) {
  const syncChannels = ApiService.communicationChannel.useSyncChannel();
  const updateDesk = ApiService.communicationChannel.useUpdateDesk();
  const delteChannel = ApiService.communicationChannel.useDeleteChannel();
  const qc = useQueryClient();
  // console.log({ channels });
  const grouped = useMemo(() => {
    const map: Partial<Record<ChannelType, IChannel[]>> = {};
    for (const ch of channels) {
      (map[ch.type] ??= []).push(ch);
    }
    return map;
  }, [channels]);

  const collapseItems = TYPE_ORDER.filter((type) => (grouped[type]?.length ?? 0) > 0).map((type) => {
    const list = grouped[type]!;
    const { icon, label } = CHANNEL_META[type];

    return {
      key: type,
      label: (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2.5">
            {icon}
            <span style={{ fontSize: 13, color: "oklch(0.4 0.02 255)" }}>{label}</span>
          </div>
          {/* count badge */}
          <span
            style={{
              background: "oklch(0.92 0.006 240)",
              color: "oklch(0.4 0.02 255)",
              borderRadius: 10,
              padding: "1px 7px",
              fontSize: 11,
              fontWeight: 600,
              lineHeight: "18px",
              minWidth: 20,
              textAlign: "center",
            }}
          >
            {list.length}
          </span>
        </div>
      ),
      children: (
        <div className="max-h-[400px] overflow-y-auto">
          {list.map((ch, i) => (
            <div
              key={ch._id}
              style={{
                padding: "10px 14px",
                borderBottom: i < list.length - 1 ? "1px solid oklch(0.94 0.005 240)" : "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              {/* ── Left: status dot + label + meta ── */}
              <div className="flex items-start gap-2.5 min-w-0">
                <span
                  className="shrink-0"
                  style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", marginTop: 5 }}
                />

                <div className="min-w-0">
                  <Typography.Text
                    className="block truncate"
                    style={{ fontSize: 12.5, fontWeight: 500, color: "oklch(0.25 0.02 255)" }}
                  >
                    {ch.type === "email"
                      ? ch?.alias
                        ? ch.alias
                        : ch.label
                      : ch?.alias
                        ? ch?.alias
                        : phoneFormatter(ch.label)}

                    {ch.provider && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 10,
                          fontWeight: 600,
                          padding: "1px 6px",
                          borderRadius: 4,
                          textTransform: "capitalize",
                          background: "oklch(0.92 0.006 240)",
                          color: "oklch(0.45 0.015 255)",
                        }}
                      >
                        {ch.provider}
                      </span>
                    )}
                  </Typography.Text>
                  {ch?.alias && (
                    <div className="flex items-center gap-1">
                      {ch.type === "email" ? <TbMail size={12} /> : <TbPhone size={12} />}
                      <Typography.Text copyable className="block truncate" type="secondary" style={{ fontSize: 11 }}>
                        {ch.type === "email" ? ch.label : phoneFormatter(ch.label)}
                      </Typography.Text>
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 mt-[3px] flex-wrap">
                    {/* Last synced */}
                    {ch.type === "email" && (
                      <Typography.Text style={{ fontSize: 11, color: "oklch(0.62 0.01 255)" }}>
                        {ch.lastSyncedAt ? `Synced ${timeAgo(ch.lastSyncedAt)} ago` : "Never synced"}
                      </Typography.Text>
                    )}
                    {deskDetails &&
                      deskDetails.defaultEmailChannelId &&
                      deskDetails.defaultEmailChannelId === ch._id && (
                        <div className="px-1 rounded bg-indigo-500">
                          <Typography.Text type="secondary" className="text-[10px] !text-white">
                            Default Channel
                          </Typography.Text>
                        </div>
                      )}
                    {deskDetails && deskDetails.defaultSmsChannelId && deskDetails.defaultSmsChannelId === ch._id && (
                      <div className="px-1 rounded bg-indigo-500">
                        <Typography.Text type="secondary" className="text-[10px] !text-white">
                          Default Channel
                        </Typography.Text>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Right: sync button ── */}
              {ch.type === "email" ? (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "sync",
                        label: "Sync",
                        icon: <TbRefresh size={12} />,
                        onClick: () =>
                          syncChannels.mutate(
                            { channel_id: ch._id, desk_id },
                            { onSuccess: () => message.success("Email synced in queue") },
                          ),
                      },
                      {
                        key: "default",
                        label: "Set as default email channel",
                        icon: <TiTickOutline size={12} />,
                        disabled: deskDetails?.defaultEmailChannelId === ch._id,
                        onClick: () =>
                          updateDesk.mutate(
                            { desk_id, body: { defaultEmailChannelId: ch._id } },
                            {
                              onSuccess: () => {
                                message.success(`${ch.label} is defalut channel now`);
                                qc.invalidateQueries({ queryKey: ["desks", desk_id] });
                              },
                            },
                          ),
                      },
                      {
                        key: "update-channel",
                        label: "Update Channel",
                        icon: <TbRefresh size={12} />,

                        onClick: () => {
                          selectChannel?.(ch);
                          openModal();
                        },
                        // updateDesk.mutate(
                        //   { desk_id, body: { defaultEmailChannelId: ch._id } },
                        //   {
                        //     onSuccess: () => {
                        //       message.success(`${ch.label} is defalut channel now`);
                        //       qc.invalidateQueries({ queryKey: ["desks", desk_id] });
                        //     },
                        //   },
                        // ),
                      },
                      { type: "divider" },
                      {
                        key: "delete",
                        label: "Remove Channel",
                        icon: <TbTrash size={12} />,
                        danger: true,
                        onClick: () => {
                          Modal.confirm({
                            title: `Remove "${ch.label}"?`,
                            content: (
                              <div>
                                <p>Are you sure you want to remove this channel?</p>
                                <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "8px" }}>
                                  This action cannot be undone.
                                </p>
                              </div>
                            ),
                            okText: "Yes, Remove",
                            okType: "danger",
                            cancelText: "Cancel",
                            onOk: () => {
                              delteChannel.mutate(
                                { channel_id: ch._id, desk_id },
                                {
                                  onSuccess: () => {
                                    message.success(`${ch.label} is removed`);
                                    qc.invalidateQueries({ queryKey: ["desks", desk_id] });
                                  },
                                },
                              );
                            },
                          });
                        },
                      },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <Button
                    size="small"
                    icon={<TbDotsVertical size={14} />}
                    loading={
                      (syncChannels.variables?.channel_id === ch._id && syncChannels.isPending) ||
                      (updateDesk.isPending && updateDesk.variables.body.defaultEmailChannelId === ch._id)
                    }
                    style={{ flexShrink: 0 }}
                  />
                </Dropdown>
              ) : (
                <Dropdown
                  menu={{
                    items: [
                      // {
                      //   key: "sync",
                      //   label: "Sync",
                      //   icon: <TbRefresh size={12} />,
                      //   onClick: () =>
                      //     syncChannels.mutate(
                      //       { channel_id: ch._id, desk_id },
                      //       { onSuccess: () => message.success("Email synced in queue") },
                      //     ),
                      // },
                      {
                        key: "default",
                        label: "Set as default sms channel",
                        icon: <TiTickOutline size={12} />,
                        disabled: deskDetails?.defaultSmsChannelId === ch._id,
                        onClick: () =>
                          updateDesk.mutate(
                            { desk_id, body: { defaultSmsChannelId: ch._id } },
                            {
                              onSuccess: () => {
                                message.success(`${ch.label} is defalut channel now`);
                                qc.invalidateQueries({ queryKey: ["desks", desk_id] });
                              },
                            },
                          ),
                      },
                      { type: "divider" },
                      {
                        key: "delete",
                        label: "Remove Channel",
                        icon: <TbTrash size={12} />,
                        danger: true,
                        onClick: () => {
                          Modal.confirm({
                            title: `Remove "${ch.label}"?`,
                            content: (
                              <div>
                                <p>Are you sure you want to remove this channel?</p>
                                <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "8px" }}>
                                  This action cannot be undone.
                                </p>
                              </div>
                            ),
                            okText: "Yes, Remove",
                            okType: "danger",
                            cancelText: "Cancel",
                            onOk: () => {
                              delteChannel.mutate(
                                { channel_id: ch._id, desk_id },
                                {
                                  onSuccess: () => {
                                    message.success(`${ch.label} is removed`);
                                    qc.invalidateQueries({ queryKey: ["desks", desk_id] });
                                  },
                                },
                              );
                            },
                          });
                        },
                      },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <Button
                    size="small"
                    icon={<TbDotsVertical size={14} />}
                    loading={updateDesk.isPending && updateDesk.variables.body.defaultSmsChannelId === ch._id}
                    style={{ flexShrink: 0 }}
                  />
                </Dropdown>
              )}
            </div>
          ))}
        </div>
      ),
    };
  });

  return (
    <>
      <style>{`
        .channels-collapse .ant-collapse-item {
          border-radius: 10px !important;
          border: 1px solid oklch(0.92 0.006 240) !important;
          overflow: hidden;
        }
        .channels-collapse .ant-collapse-item + .ant-collapse-item {
          margin-top: 8px;
        }
        .channels-collapse .ant-collapse-header {
          padding: 12px 14px !important;
          background: oklch(0.97 0.004 240) !important;
          align-items: center !important;
        }
        .channels-collapse .ant-collapse-header:hover {
          background: oklch(0.95 0.005 240) !important;
        }
        .channels-collapse .ant-collapse-content {
          border-top: 1px solid oklch(0.92 0.006 240) !important;
        }
        .channels-collapse .ant-collapse-content-box {
          padding: 0 !important;
        }
        .channels-collapse .ant-collapse-expand-icon {
          padding-inline-end: 8px !important;
        }
      `}</style>

      <div className="flex flex-col gap-3">
        <Text
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "oklch(0.6 0.01 255)",
          }}
        >
          Channels
        </Text>

        {channels.length === 0 ? (
          <div className="flex w-full justify-center py-12">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No account connected yet" />
          </div>
        ) : (
          <Collapse
            className="channels-collapse"
            expandIcon={({ isActive }) => (
              <TbChevronRight
                size={12}
                style={{
                  color: "oklch(0.65 0.01 255)",
                  transition: "transform 0.2s",
                  transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
                }}
              />
            )}
            style={{ background: "transparent", border: "none" }}
            items={collapseItems}
          />
        )}
      </div>
    </>
  );
}
// Formatter utility
const formatE164 = (raw: string): string => {
  // Strip everything except digits and leading +
  const digits = raw.replace(/[^\d]/g, "");
  if (!digits) return "";
  // Reconstruct E.164 — always prepend +
  return `+${digits}`;
};

const parseDisplayPhone = (e164: string): string => {
  // Display as +1 555 000 0000 style while editing
  const digits = e164.replace(/[^\d]/g, "");
  if (digits.length === 0) return "";
  if (digits.length <= 1) return `+${digits}`;
  if (digits.length <= 4) return `+${digits.slice(0, 1)} ${digits.slice(1)}`;
  if (digits.length <= 7) return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4)}`;
  return `+${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
};
export default function NotificationChannels() {
  const { orgcode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const desk_id = searchParams.get("desk_id") ?? "";
  const orgCode = searchParams.get("orgCode");
  const orgDetailsRes = ApiService.organisation.useGetBySlug(orgcode);
  const organisations = ApiService.organisation.useGetAllOrganisations();
  const createGsuiteChannel = ApiService.communicationChannel.useCreateGsuiteChannel();
  const orgDetails = orgDetailsRes.data;
  const createChannel = ApiService.communicationChannel.useCreateChannel();
  const addGsuiteFromExisting = ApiService.communicationChannel.useAddGsuiteFromExisting();
  const getDeskDeatails = ApiService.communicationChannel.useGetDeskByID({ desk_id });
  const deskDetails = getDeskDeatails.data?.result;
  const checkIdDeskExistsOrNot = ApiService.communicationChannel.useCheckDeskCorrelationExists();

  const createDesk = ApiService.communicationChannel.useCreateDesk();
  // panel open/collapsed state — not form data, stays as plain useState
  const [googleOpen, setGoogleOpen] = useState(false);
  const [microsoftOpen, setMicrosoftOpen] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [twilioOpen, setTwilioOpen] = useState(false);
  const [serviceFile, setServiceFile] = useState<File | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<IChannel | null>(null);
  const [isUpdateModalOpen, seIsUpdateModalOpen] = useState(false);

  const [googleStatus, setGoogleStatus] = useState<ConnStatus>("idle");

  const [googleServerError, setGoogleServerError] = useState<string>();

  const [microsoftStatus, setMicrosoftStatus] = useState<ConnStatus>("idle");

  const [microsoftServerError, setMicrosoftServerError] = useState<string>();
  const [jsonMessage, setJsonMessage] = useState<{ type: "error" | "success"; text: string } | null>(null);
  const [jsonStatus, setJsonStatus] = useState<ConnStatus>("idle");
  const [gsuiteMode, setGsuiteMode] = useState<"new" | "existing">("new");
  const [twilioStatus, setTwilioStatus] = useState<ConnStatus>("error");

  // one antd Form per provider card — this now owns all the actual field data
  const [googleForm] = Form.useForm<GoogleValues>();
  const [microsoftForm] = Form.useForm<MicrosoftValues>();
  const [jsonForm] = Form.useForm<JsonValues>();
  const [twilioForm] = Form.useForm<TwilioValues>();
  const [fromExistingForm] = Form.useForm<{ sourceChannelId: string; impersonateEmail: string; alias?: string }>();

  const existingGsuiteChannels = useMemo(
    () => (deskDetails?.channels ?? []).filter((ch) => ch.provider === "gsuite"),
    [deskDetails],
  );
  const hasExistingGsuite = existingGsuiteChannels.length > 0;

  const navigate = useNavigate();
  const handleGoogleFinish = (_values: GoogleValues) => {
    const trimmedPassword = _values.appPassword.replace(/\s+/g, "")?.trim();

    setGoogleStatus("loading");
    createChannel.mutate(
      {
        desk_id: desk_id,
        body: {
          type: "email",
          config: {
            provider: "gmail",
            user: _values.email,
            pass: trimmedPassword,
          },
          alias: _values.alias,
        },
      },
      {
        onSuccess: () => {
          message.success("Google account connected sucessfully");
          qc.invalidateQueries({ queryKey: ["desks", desk_id] });
          googleForm.resetFields();
        },
        onError: () => {
          setGoogleStatus("error");
        },
      },
    );
  };

  const handleMicrosoftFinish = (_values: MicrosoftValues) => {
    setMicrosoftServerError(undefined);
    setMicrosoftStatus("loading");
    createChannel.mutate(
      {
        desk_id,
        body: {
          type: "email",
          config: {
            provider: "microsoft",
            user: _values.email,
            tenantId: _values.tenantId,
            clientId: _values.clientId,
            clientSecret: _values.clientSecret,
          },
          alias: _values.alias,
        },
      },
      {
        onSuccess: () => {
          message.success("Microsoft account connected successfully");
          setMicrosoftStatus("connected");
          setMicrosoftOpen(false);
          qc.invalidateQueries({ queryKey: ["desks", desk_id] });
          microsoftForm.resetFields();
        },
        onError: () => {
          setMicrosoftStatus("error");
          setMicrosoftServerError("Failed to connect Microsoft account. Check your credentials.");
        },
      },
    );
  };

  const handleJsonFinish = (values: JsonValues) => {
    console.log({ abc: values.impersonateEmails });
    const file = values.file;

    setJsonMessage(null); // clear previous message on each attempt

    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setJsonMessage({ type: "error", text: `File exceeds 5MB limit: ${file.name}` });
      return;
    }

    const isJson = file.type === "application/json" || file.name.toLowerCase().endsWith(".json");
    if (!isJson) {
      setJsonMessage({ type: "error", text: `${file.name} is not a valid JSON file` });
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target?.result as string);

        if (!parsed.private_key || typeof parsed.private_key !== "string") {
          setJsonMessage({ type: "error", text: `${file.name}: Missing required field "private_key"` });
          return;
        }

        if (!parsed.client_email || typeof parsed.client_email !== "string") {
          setJsonMessage({ type: "error", text: `${file.name}: Missing required field "client_email"` });
          return;
        }

        if (!parsed.private_key.includes("BEGIN PRIVATE KEY")) {
          setJsonMessage({ type: "error", text: `${file.name}: Invalid private key format` });
          return;
        }

        if (!parsed.client_email.includes("@")) {
          setJsonMessage({ type: "error", text: `${file.name}: Invalid client email` });
          return;
        }

        // setParsedFileData(parsed);

        createChannel.mutate(
          {
            desk_id: desk_id!,
            body: {
              type: "email",
              config: {
                provider: "gsuite",
                serviceAccountJson: {
                  client_email: parsed.client_email,
                  private_key: parsed.private_key,
                  private_key_id: parsed.private_key_id ?? "",
                  project_id: parsed.project_id ?? "",
                  client_id: parsed.client_id ?? "",
                },
                impersonateEmails: values.impersonateEmails,
              },
            },
          },
          {
            onSuccess: () => {
              message.success("Service files added");
              qc.invalidateQueries({ queryKey: ["desks", desk_id] });
              jsonForm.resetFields();
            },
            onError: () => {
              setJsonStatus("error");
            },
          },
        );
      } catch {
        setJsonMessage({ type: "error", text: `${file.name} contains invalid JSON` });
      }
    };

    reader.readAsText(file);
  };

  const handleTwilioFinish = (_values: TwilioValues) => {
    setTwilioStatus("loading");
    createChannel.mutate(
      {
        desk_id,
        body: {
          type: "sms",
          config: {
            accountSid: _values.sid,
            authToken: _values.token,
            ...(_values.messagingServiceSid && { messagingServiceSid: _values.messagingServiceSid }),
            numbers: _values.numbers.map((n) => ({
              number: n.number,
              ...(n.alias && { alias: n.alias }),
            })),
          },
        },
      },
      {
        onSuccess: (res) => {
          const result = res.result as unknown as {
            type: string;
            created: unknown[];
            skipped: unknown[];
            failed: { number: string; error: string }[];
          };

          if (result.type === "multi") {
            if (result.created.length === 0) {
              message.error(`All numbers failed verification. ${result.failed[0]?.error ?? ""}`);
              setTwilioStatus("error");
            } else if (result.failed.length > 0) {
              message.warning(`${result.created.length} number(s) connected, ${result.failed.length} failed.`);
              setTwilioStatus("connected");
              qc.invalidateQueries({ queryKey: ["desks", desk_id] });
              twilioForm.resetFields();
            } else {
              message.success(`${result.created.length} number(s) connected successfully`);
              setTwilioStatus("connected");
              qc.invalidateQueries({ queryKey: ["desks", desk_id] });
              twilioForm.resetFields();
            }
          } else {
            // single channel fallback
            message.success("Twilio account connected successfully");
            setTwilioStatus("connected");
            qc.invalidateQueries({ queryKey: ["desks", desk_id] });
            twilioForm.resetFields();
          }
        },
        onError: () => {
          setTwilioStatus("error");
        },
      },
    );
  };

  return (
    <DashboardLayout
      headerRight={
        <div className="!mt-2 flex items-center gap-2 justify-end">
          <OrgSelector
            organisations={organisations.data ?? []}
            selected={orgCode as string}
            onChange={(org) => {
              checkIdDeskExistsOrNot.mutate(
                { correlationId: org.code },
                {
                  onSuccess: (res) => {
                    console.log({ res });
                    if (res.result.exists) {
                      navigate(
                        `/dashboard/organisation/${org.code}/channel?desk_id=${res.result.desk._id}&orgCode=${org.code}&src=org`,
                        { replace: true },
                      );
                    } else {
                      createDesk.mutate(
                        {
                          correlation_id: org.code,
                          meta: {
                            name: org.name,
                            code: org.code,
                            slug: org.slug,
                            logo: org.meta?.logo,
                            favicon: org.meta?.favicon,
                          },
                        },
                        {
                          onSuccess: (res) => {
                            navigate(
                              `/dashboard/organisation/${org.code}/channel?desk_id=${res.result._id}&orgCode=${org.code}&src=org`,
                              { replace: true },
                            );
                          },
                        },
                      );
                    }
                  },
                },
              );
            }}
          />
          <Button
            href={`/dashboard/communications/conversations?orgCode=${orgCode}`}
            type="primary"
            iconPosition="end"
            icon={<TbChevronRight />}
          >
            Communication Portal
          </Button>
        </div>
      }
    >
      <PageTitle
        title="Communication Channels"
        pageTitle={[{ title: "Organisations", link: "/dashboard/organisations" }, { title: "Communication Channels" }]}
      />
      <div
        className="flex h-[calc(100vh-60px)]"
        style={{ fontFamily: "'DM Sans', sans-serif", background: "oklch(0.96 0.006 240)" }}
      >
        <style>{`
        @keyframes dc-fade-slide-down { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dc-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>

        {/* LEFT PANEL: Org profile */}
        <div
          className="flex flex-col"
          style={{
            width: "33.333%",
            minWidth: 280,
            background: "#fff",
            borderRight: "1px solid oklch(0.9 0.006 240)",
            padding: "48px 36px",
          }}
        >
          <div className="flex flex-col gap-8 flex-1">
            <div className="flex flex-col items-start gap-5">
              {/* <Avatar src={orgDetails?.meta?.logo}></Avatar> */}
              <img src={orgDetails?.meta?.logo} className="!w-1/2" />
              <div className="flex flex-col gap-1.5">
                <Text
                  style={{
                    fontSize: 22,
                    fontWeight: 600,
                    color: "oklch(0.15 0.02 255)",
                    letterSpacing: "-0.4px",
                    display: "block",
                  }}
                >
                  {orgDetails?.name}
                </Text>
              </div>
            </div>

            <div style={{ height: 1, background: "oklch(0.9 0.006 240)" }} />

            <ChannelsSection
              channels={deskDetails?.channels ?? []}
              desk_id={desk_id}
              deskDetails={deskDetails}
              openModal={() => seIsUpdateModalOpen(true)}
              selectChannel={(val) => setSelectedChannel(val)}
            />

            <div
              className="mt-auto rounded-[10px]"
              style={{ padding: 14, background: "oklch(0.97 0.004 240)", border: "1px solid oklch(0.92 0.006 240)" }}
            >
              <Text style={{ fontSize: 12, color: "oklch(0.5 0.02 255)", lineHeight: 1.6 }}>
                Configure email and SMS channels to enable communication for your organization.
              </Text>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Configuration */}
        <div className="flex-1 flex flex-col gap-10" style={{ padding: "48px 52px", overflowY: "auto" }}>
          <div className="flex flex-col gap-1.5">
            <Text
              style={{
                fontSize: 26,
                fontWeight: 600,
                color: "oklch(0.15 0.02 255)",
                letterSpacing: "-0.5px",
                display: "block",
              }}
            >
              Communication Channels
            </Text>
            <Text style={{ fontSize: 14, color: "oklch(0.5 0.02 255)" }}>
              Connect email and SMS providers to send notifications from your organization.
            </Text>
          </div>

          {/* EMAIL SECTION */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "oklch(0.45 0.02 255)",
                }}
              >
                Email
              </Text>
              <div className="flex-1" style={{ height: 1, background: "oklch(0.88 0.01 240)" }} />
            </div>

            {/* Google */}
            <ProviderCard
              icon={<GoogleLogo />}
              iconBg="oklch(0.97 0.01 25)"
              title="Google"
              subtitle="Gmail with app password"
              status={googleStatus}
              open={googleOpen}
              onToggle={() => setGoogleOpen((v) => !v)}
            >
              <Form
                form={googleForm}
                layout="vertical"
                initialValues={{ email: "", appPassword: "" }}
                onFinish={handleGoogleFinish}
              >
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[{ required: true, message: "Email address is required." }]}
                >
                  <Input placeholder="you@gmail.com" style={inputStyle} />
                </Form.Item>
                <Form.Item
                  name="appPassword"
                  label="App Password"
                  rules={[{ required: true, message: "App password is required." }]}
                  extra={
                    <FieldHint>
                      Generate an app password in your Google Account → Security → 2-Step Verification
                    </FieldHint>
                  }
                >
                  <Input.Password placeholder="xxxx xxxx xxxx xxxx" style={monoInputStyle} />
                </Form.Item>
                <Form.Item name="alias" label="Alias">
                  <Input placeholder="John Doe" style={inputStyle} />
                </Form.Item>
                <Form.Item className="mb-0">
                  <div className="flex items-center gap-2.5">
                    {/* <SaveButton  label={googleSaving ? "Saving…" : "Save"} /> */}
                    <Button loading={createChannel.isPending} icon={<TbPlus />} htmlType="submit" type="primary">
                      Save
                    </Button>
                    <ServerError message={googleServerError} />
                  </div>
                </Form.Item>
              </Form>
            </ProviderCard>

            {/* Microsoft */}
            <Tooltip title="This integration will be available in a future update" placement="top">
              <ProviderCard
                icon={<MicrosoftLogo />}
                iconBg="oklch(0.97 0.005 240)"
                title="Microsoft"
                subtitle="Outlook / Azure AD — client credentials"
                status={microsoftStatus}
                open={microsoftOpen}
                onToggle={() => setMicrosoftOpen((v) => !v)}
              >
                <Form form={microsoftForm} layout="vertical" onFinish={handleMicrosoftFinish}>
                  <Form.Item
                    name="email"
                    label="Mailbox Email"
                    rules={[
                      { required: true, message: "Mailbox email is required." },
                      { type: "email", message: "Enter a valid email address." },
                    ]}
                  >
                    <Input placeholder="support@organization.com" style={inputStyle} />
                  </Form.Item>
                  <Form.Item
                    name="tenantId"
                    label="Tenant ID"
                    rules={[{ required: true, message: "Tenant ID is required." }]}
                  >
                    <Input
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      style={{ ...monoInputStyle, fontSize: 13 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="clientId"
                    label="Client ID"
                    rules={[{ required: true, message: "Client ID is required." }]}
                  >
                    <Input
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      style={{ ...monoInputStyle, fontSize: 13 }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="clientSecret"
                    label="Client Secret"
                    rules={[{ required: true, message: "Client secret is required." }]}
                  >
                    <Input.Password placeholder="••••••••••••••••••••••" style={monoInputStyle} />
                  </Form.Item>
                  <Form.Item name="alias" label="Alias">
                    <Input placeholder="Support Team (optional)" style={inputStyle} />
                  </Form.Item>
                  <Form.Item className="mb-0">
                    <div className="flex items-center gap-2.5">
                      <Button loading={createChannel.isPending} icon={<TbPlus />} htmlType="submit" type="primary">
                        Save
                      </Button>
                      <ServerError message={microsoftServerError} />
                    </div>
                  </Form.Item>
                </Form>
              </ProviderCard>
            </Tooltip>

            {/* Service Account JSON */}
            <Tooltip title="This integration will be available in a future update" placement="top">
              <ProviderCard
                icon={<ServiceJsonLogo />}
                iconBg="oklch(0.97 0.012 145)"
                title="Service Account JSON"
                subtitle="Google service account credentials file"
                status={jsonStatus}
                open={jsonOpen}
                onToggle={() => {
                  setJsonOpen((v) => !v);
                  setJsonMessage(null);
                }}
              >
                {/* Mode toggle */}
                <div className="flex items-center justify-between mb-5">
                  <Tooltip
                    title={
                      !hasExistingGsuite ? "Add a GSuite service account first before using this option" : undefined
                    }
                  >
                    <Segmented
                      options={[
                        { label: "New service account", value: "new" },
                        { label: "Add email to existing", value: "existing", disabled: !hasExistingGsuite },
                      ]}
                      value={gsuiteMode}
                      onChange={(val) => {
                        setGsuiteMode(val as "new" | "existing");
                        jsonForm.resetFields();
                        fromExistingForm.resetFields();
                        setJsonMessage(null);
                      }}
                    />
                  </Tooltip>
                </div>

                {gsuiteMode === "new" ? (
                  <Form
                    form={jsonForm}
                    layout="vertical"
                    initialValues={{ file: null, impersonateEmails: [{ email: "", alias: "" }] }}
                    onFinish={handleJsonFinish}
                  >
                    <Form.Item name="file" rules={[{ required: true, message: "Please upload a JSON file." }]}>
                      <JsonDropzone
                        onChange={(file) => {
                          setServiceFile(file ?? null);
                          setJsonMessage(null);
                        }}
                      />
                    </Form.Item>

                    <Form.List
                      name="impersonateEmails"
                      rules={[
                        {
                          validator: async (_, items: { email: string; alias: string }[]) => {
                            if (!items || items.length < 1 || items.every((e) => !e?.email?.trim())) {
                              return Promise.reject(new Error("At least one email is required"));
                            }
                          },
                        },
                      ]}
                    >
                      {(fields, { add, remove }, { errors }) => (
                        <div className="flex flex-col gap-2">
                          <label className="text-sm font-medium text-gray-700">Emails</label>
                          {fields.map((field) => (
                            <div key={field.key} className="flex gap-2 items-start">
                              <Form.Item
                                name={[field.name, "email"]}
                                className="mb-0 flex-1"
                                rules={[
                                  { required: true, message: "Email is required" },
                                  { type: "email", message: "Enter a valid email address" },
                                ]}
                              >
                                <Input placeholder="john@workspace.com" />
                              </Form.Item>
                              <Form.Item name={[field.name, "alias"]} className="mb-0 flex-1">
                                <Input placeholder="Alias (optional)" />
                              </Form.Item>
                              {fields.length > 1 && (
                                <MinusCircleOutlined
                                  className="text-gray-400 hover:text-red-500 cursor-pointer mt-2"
                                  onClick={() => remove(field.name)}
                                />
                              )}
                            </div>
                          ))}
                          <Form.ErrorList errors={errors} />
                          <div className="flex justify-end">
                            <Button
                              type="dashed"
                              onClick={() => add({ email: "", alias: "" })}
                              icon={<PlusOutlined />}
                              className="w-fit"
                            >
                              Add Email
                            </Button>
                          </div>
                        </div>
                      )}
                    </Form.List>

                    {jsonMessage && (
                      <p className={`mt-2 text-sm ${jsonMessage.type === "error" ? "text-red-500" : "text-green-600"}`}>
                        {jsonMessage.type === "error" ? "⚠ " : "✓ "}
                        {jsonMessage.text}
                      </p>
                    )}

                    <Form.Item className="mb-0 mt-4">
                      <Button
                        htmlType="submit"
                        type="primary"
                        disabled={!serviceFile}
                        loading={createChannel.isPending}
                      >
                        Upload
                      </Button>
                    </Form.Item>
                  </Form>
                ) : (
                  <Form
                    form={fromExistingForm}
                    layout="vertical"
                    onFinish={(values) => {
                      addGsuiteFromExisting.mutate(
                        {
                          desk_id,
                          body: {
                            sourceChannelId: values.sourceChannelId,
                            impersonateEmail: values.impersonateEmail,
                            alias: values.alias ?? null,
                          },
                        },
                        {
                          onSuccess: () => {
                            message.success("GSuite email added successfully");
                            qc.invalidateQueries({ queryKey: ["desks", desk_id] });
                            fromExistingForm.resetFields();
                          },
                          onError: (err: unknown) => {
                            const msg = err instanceof Error ? err.message : "Failed to add GSuite email";
                            message.error(msg);
                          },
                        },
                      );
                    }}
                  >
                    <Form.Item
                      name="sourceChannelId"
                      label="Source GSuite Account"
                      rules={[{ required: true, message: "Please select a source GSuite account" }]}
                    >
                      <Select placeholder="Select an existing GSuite channel">
                        {existingGsuiteChannels.map((ch) => (
                          <Select.Option key={ch._id} value={ch._id}>
                            {ch.alias ?? ch.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item
                      name="impersonateEmail"
                      label="Email"
                      rules={[
                        { required: true, message: "Email is required" },
                        { type: "email", message: "Enter a valid email address" },
                      ]}
                    >
                      <Input placeholder="john@workspace.com" style={inputStyle} />
                    </Form.Item>

                    <Form.Item name="alias" label="Alias">
                      <Input placeholder="John Doe (optional)" style={inputStyle} />
                    </Form.Item>

                    <Form.Item className="mb-0">
                      <Button
                        htmlType="submit"
                        type="primary"
                        icon={<TbPlus />}
                        loading={addGsuiteFromExisting.isPending}
                      >
                        Add Email
                      </Button>
                    </Form.Item>
                  </Form>
                )}
              </ProviderCard>
            </Tooltip>
          </div>

          {/* SMS SECTION */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Text
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "oklch(0.45 0.02 255)",
                }}
              >
                SMS
              </Text>
              <div className="flex-1" style={{ height: 1, background: "oklch(0.88 0.01 240)" }} />
            </div>

            {/* Twilio */}
            <ProviderCard
              icon={<TwilioLogo />}
              iconBg="oklch(0.96 0.015 25)"
              title="Twilio"
              subtitle="SMS via Twilio messaging API"
              status={twilioStatus}
              open={twilioOpen}
              onToggle={() => setTwilioOpen((v) => !v)}
            >
              <Form
                form={twilioForm}
                layout="vertical"
                initialValues={{ sid: "", token: "", numbers: [{ number: "" }] }}
                onFinish={handleTwilioFinish}
              >
                <Form.Item
                  name="sid"
                  label="Account SID"
                  rules={[{ required: true, message: "Account SID is required." }]}
                >
                  <Input placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={{ ...monoInputStyle, fontSize: 13 }} />
                </Form.Item>

                <Form.Item
                  name="token"
                  label="Auth Token"
                  rules={[{ required: true, message: "Auth token is required." }]}
                >
                  <Input.Password placeholder="••••••••••••••••••••••••••••••••" style={monoInputStyle} />
                </Form.Item>

                <Form.Item
                  name="messagingServiceSid"
                  label="Messaging Service SID"
                  extra={<FieldHint>Optional — required for A2P 10DLC compliance</FieldHint>}
                >
                  <Input placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={{ ...monoInputStyle, fontSize: 13 }} />
                </Form.Item>

                <Form.List
                  name="numbers"
                  rules={[
                    {
                      validator: async (_, items: { number: string }[]) => {
                        if (!items || items.length < 1 || items.every((n) => !n?.number?.trim())) {
                          return Promise.reject(new Error("At least one number is required"));
                        }
                      },
                    },
                  ]}
                >
                  {(fields, { add, remove }, { errors }) => (
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-medium text-gray-700">Phone Numbers</label>
                      {fields.map((field) => (
                        <div key={field.key} className="flex gap-2 items-start">
                          <Form.Item
                            name={[field.name, "number"]}
                            className="mb-0 flex-1"
                            rules={[
                              { required: true, message: "Number is required" },
                              {
                                validator: (_, value) => {
                                  if (!value) return Promise.resolve();
                                  return /^\+[1-9]\d{6,14}$/.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject("Enter a valid E.164 number (e.g. +15550000000)");
                                },
                              },
                            ]}
                            getValueFromEvent={(e) => formatE164(e.target.value)}
                          >
                            <Input
                              placeholder="+1 555 000 0000"
                              style={monoInputStyle}
                              onChange={(e) => {
                                e.target.value = parseDisplayPhone(e.target.value);
                              }}
                            />
                          </Form.Item>
                          <Form.Item name={[field.name, "alias"]} className="mb-0 flex-1">
                            <Input placeholder="Alias (optional)" style={inputStyle} />
                          </Form.Item>
                          {fields.length > 1 && (
                            <MinusCircleOutlined
                              className="text-gray-400 hover:text-red-500 cursor-pointer mt-2"
                              onClick={() => remove(field.name)}
                            />
                          )}
                        </div>
                      ))}
                      <Form.ErrorList errors={errors} />
                      <div className="flex justify-end">
                        <Button
                          type="dashed"
                          onClick={() => add({ number: "" })}
                          icon={<PlusOutlined />}
                          className="w-fit"
                        >
                          Add Number
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.List>

                <Form.Item className="mb-0 mt-4">
                  <div className="flex items-center gap-2.5">
                    <Button loading={createChannel.isPending} htmlType="submit" icon={<TbPlus />} type="primary">
                      Save
                    </Button>
                  </div>
                </Form.Item>
              </Form>
            </ProviderCard>
          </div>

          <div style={{ height: 24 }} />
        </div>
      </div>
      <UpdateChannelDetails
        onClose={() => seIsUpdateModalOpen(false)}
        selectedChannel={selectedChannel}
        open={isUpdateModalOpen}
        desk_Id={desk_id}
      />
    </DashboardLayout>
  );
}

const UpdateChannelDetails = ({
  onClose,
  open,
  selectedChannel,
  desk_Id,
}: {
  open: boolean;
  onClose: () => void;
  selectedChannel: IChannel | null;
  desk_Id?: string;
}) => {
  const qc = useQueryClient();
  const updateChannel = ApiService.communicationChannel.useUpdateChannelAlias();
  const [form] = useForm();
  useEffect(() => {
    if (selectedChannel) {
      form.setFieldsValue({ alias: selectedChannel.alias ?? "", val: selectedChannel.label });
    } else {
      form.resetFields();
    }
  }, [selectedChannel]);

  return (
    <Modal
      open={open}
      onOk={form.submit}
      onCancel={onClose}
      centered
      title="Update Channel"
      okButtonProps={{ loading: updateChannel.isPending }}
      okText="Update"
    >
      <Form
        onFinish={(val) => {
          updateChannel.mutate(
            {
              channel_id: selectedChannel?._id as string,
              desk_id: desk_Id as string,
              body: { alias: val.alias },
            },
            {
              onSuccess: () => {
                message.success("channel Updated sucessfully");
                qc.invalidateQueries({ queryKey: ["desks", desk_Id] });
                onClose();
              },
            },
          );
        }}
        form={form}
        layout="vertical"
      >
        <Form.Item label="Alias" name={"alias"}>
          <Input />
        </Form.Item>
        <Form.Item label={selectedChannel?.type === "email" ? "Email" : "Phone"} name={"val"}>
          <Input disabled readOnly />
        </Form.Item>
      </Form>
    </Modal>
  );
};
